import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Search, Send, MailOpen, UserCheck, Bell, Clock, Copy, Check, ArrowLeft, Loader2, Mail,
} from 'lucide-react';
import { api, formatApiError } from '../../lib/api';

const inviteLink = (token) => `${window.location.origin}/invitacion/${token}`;

function getInviteStatus(inv) {
  const now = new Date().toISOString();
  if (inv.accepted_at) return { key: 'activated', label: 'Activada', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
  if ((inv.expires_at || '') < now) return { key: 'expired', label: 'Expirada', cls: 'bg-rose-50 text-rose-600 border-rose-200' };
  if (inv.reminder_sent_at) return { key: 'reminded', label: 'Recordada', cls: 'bg-amber-50 text-amber-700 border-amber-200' };
  if (inv.opened_at) return { key: 'opened', label: 'Abierta', cls: 'bg-sky-50 text-sky-700 border-sky-200' };
  return { key: 'sent', label: 'Enviada', cls: 'bg-cmic-50 text-cmic-700 border-cmic-200' };
}

const FILTERS = [
  { key: 'sent', label: 'Enviadas', Icon: Send, color: 'text-cmic-600', bg: 'bg-cmic-50', fn: () => true },
  { key: 'opened', label: 'Abiertas', Icon: MailOpen, color: 'text-sky-600', bg: 'bg-sky-50', fn: (i) => !!i.opened_at },
  { key: 'activated', label: 'Activadas', Icon: UserCheck, color: 'text-emerald-600', bg: 'bg-emerald-50', fn: (i) => !!i.accepted_at },
  { key: 'reminded', label: 'Recordadas', Icon: Bell, color: 'text-amber-600', bg: 'bg-amber-50', fn: (i) => !!i.reminder_sent_at },
  { key: 'pending', label: 'Pendientes', Icon: Clock, color: 'text-violet-600', bg: 'bg-violet-50', fn: (i) => !i.accepted_at && (i.expires_at || '') >= new Date().toISOString() },
];

function CopyLinkButton({ token, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <button type="button" data-testid={testid}
      onClick={() => navigator.clipboard.writeText(inviteLink(token)).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1600); })}
      className={`inline-flex items-center gap-1 text-[11px] font-medium rounded-md px-2.5 py-1.5 border transition-colors ${copied ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-white text-cmic-700 border-cmic-200 hover:bg-cmic-50'}`}>
      {copied ? <><Check size={12} /> Copiado</> : <><Copy size={12} /> Copiar enlace</>}
    </button>
  );
}

export default function AdminInvitationsPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const filter = params.get('filter') || 'sent';
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true); setError('');
      try {
        const { data } = await api.get('/admin/interpreter-invitations');
        setInvitations(data || []);
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || 'Error al cargar invitaciones');
      } finally { setLoading(false); }
    })();
  }, []);

  const counts = useMemo(() => {
    const c = {};
    for (const f of FILTERS) c[f.key] = invitations.filter(f.fn).length;
    return c;
  }, [invitations]);

  const active = FILTERS.find((f) => f.key === filter) || FILTERS[0];
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return invitations.filter(active.fn).filter((i) => (
      !q || (i.interpreter_name || '').toLowerCase().includes(q) || (i.email || '').toLowerCase().includes(q)
    ));
  }, [invitations, active, query]);

  function setFilter(key) { setParams({ filter: key }); }

  return (
    <div className="space-y-5" data-testid="admin-invitations-page">
      <div>
        <button onClick={() => navigate('/admin/community-users')} data-testid="invitations-back"
          className="inline-flex items-center gap-1.5 text-sm text-charcoal-500 hover:text-cmic-700 mb-2">
          <ArrowLeft size={15} /> Volver a Usuarios
        </button>
        <h1 className="text-2xl font-bold text-charcoal-800">Estado de invitaciones</h1>
        <p className="text-charcoal-500 text-sm mt-1">Detalle de las invitaciones enviadas a intérpretes del directorio. Filtra por categoría y busca por nombre o correo.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {FILTERS.map(({ key, label, Icon, color, bg }) => (
          <button key={key} type="button" onClick={() => setFilter(key)} data-testid={`invitations-filter-${key}`}
            className={`text-left rounded-xl border p-3 flex items-center gap-3 transition-all ${filter === key ? 'border-cmic-400 ring-2 ring-cmic-100 bg-white' : 'border-gray-100 bg-white hover:border-cmic-300 hover:shadow-sm'}`}>
            <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}><Icon size={16} className={color} /></div>
            <div>
              <div className="text-xl font-bold text-charcoal-800 leading-none">{counts[key] ?? 0}</div>
              <div className="text-[11px] text-charcoal-500 mt-0.5">{label}</div>
            </div>
          </button>
        ))}
      </div>

      <div className="relative w-full sm:w-80">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-400" />
        <input type="text" placeholder="Buscar por nombre o correo…" value={query} onChange={(e) => setQuery(e.target.value)}
          data-testid="invitations-search" className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm" />
      </div>

      {error && <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-3 text-sm">{error}</div>}

      {loading ? (
        <div className="py-24 flex items-center justify-center"><Loader2 className="animate-spin text-cmic-500" size={32} /></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="invitations-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-right px-2 py-3 font-medium text-charcoal-400 text-[10px] w-10">#</th>
                  <th className="text-left px-3 py-3 font-medium text-charcoal-600">Intérprete</th>
                  <th className="text-left px-3 py-3 font-medium text-charcoal-600">Estado</th>
                  <th className="text-left px-3 py-3 font-medium text-charcoal-600">Generada</th>
                  <th className="text-right px-4 py-3 font-medium text-charcoal-600">Enlace</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((inv, idx) => {
                  const st = getInviteStatus(inv);
                  return (
                    <tr key={inv.id || inv.token} data-testid={`invitation-row-${inv.interpreter_slug}`} className="hover:bg-gray-50/60">
                      <td className="px-2 py-3 text-right text-charcoal-400 text-xs tabular-nums">{idx + 1}</td>
                      <td className="px-3 py-3">
                        <div className="font-medium text-charcoal-800">{inv.interpreter_name || inv.interpreter_slug}</div>
                        <div className="text-[11px] text-charcoal-500 flex items-center gap-1"><Mail size={10} />{inv.email || 'sin correo'}</div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap"><span className={`text-[10px] font-semibold border rounded px-2 py-0.5 ${st.cls}`}>{st.label}</span></td>
                      <td className="px-3 py-3 text-[11px] text-charcoal-500 whitespace-nowrap">
                        {inv.created_at ? new Date(inv.created_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap"><CopyLinkButton token={inv.token} testid={`invitation-copy-${inv.interpreter_slug}`} /></td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr><td colSpan="5" className="px-4 py-12 text-center text-charcoal-500 text-sm">{query ? 'Sin resultados para tu búsqueda.' : 'No hay invitaciones en esta categoría.'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
